import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { OTAMISER_LISTING_API_URL } from 'src/utils/const';

const DASHBOARD_DAYS = 14;

interface OtamiserChartRow {
  date: string;
  source: string;
  successCount: number;
  failedCount: number;
  totalCount: number;
  attemptCount: number;
  successRate: number;
  failureRate: number;
}

interface OtamiserFailureRow {
  date: string;
  source: string;
  listingID: string;
  rankingConfigID: number;
  failedAttempts: number;
  latestFailureAt: string | null;
  reason: string;
}

interface OtamiserReasonSummaryRow {
  date: string;
  source: string;
  reason: string;
  count: number;
}

interface OtamiserHealthResponse {
  days: number;
  chart: OtamiserChartRow[];
  failures: OtamiserFailureRow[];
  failureReasonSummary: OtamiserReasonSummaryRow[];
  generatedAt: string;
}

interface ChartDatum {
  date: string;
  label: string;
  airbnbSuccess: number;
  airbnbFailed: number;
  bdcSuccess: number;
  bdcFailed: number;
}

const sourceLabel = (source: string) => {
  if (source === 'booking_com') {
    return 'BDC';
  }
  if (source === 'airbnb') {
    return 'Airbnb';
  }
  return source;
};

const formatDateLabel = (date: string) => {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return date;
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const buildChartData = (rows: OtamiserChartRow[]): ChartDatum[] => {
  const byDate: Record<string, ChartDatum> = {};

  rows.forEach(row => {
    if (!byDate[row.date]) {
      byDate[row.date] = {
        date: row.date,
        label: formatDateLabel(row.date),
        airbnbSuccess: 0,
        airbnbFailed: 0,
        bdcSuccess: 0,
        bdcFailed: 0,
      };
    }

    if (row.source === 'booking_com') {
      byDate[row.date].bdcSuccess += row.successCount;
      byDate[row.date].bdcFailed += row.failedCount;
      return;
    }

    if (row.source === 'airbnb') {
      byDate[row.date].airbnbSuccess += row.successCount;
      byDate[row.date].airbnbFailed += row.failedCount;
    }
  });

  return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
};

const calculateSummary = (rows: OtamiserChartRow[]) => {
  const total = rows.reduce((sum, row) => sum + row.totalCount, 0);
  const success = rows.reduce((sum, row) => sum + row.successCount, 0);
  const failed = rows.reduce((sum, row) => sum + row.failedCount, 0);
  return {
    total,
    success,
    failed,
    successRate: total ? (success / total) * 100 : 0,
  };
};

const DashboardMetricCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const OtamiserHealthDashboard = () => {
  const [data, setData] = useState<OtamiserHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${OTAMISER_LISTING_API_URL}/dashboard/health?days=${DASHBOARD_DAYS}`
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to load dashboard');
      }
      setData(payload);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const chartData = useMemo(() => buildChartData(data?.chart || []), [data]);
  const summary = useMemo(() => calculateSummary(data?.chart || []), [data]);
  const topReasons = useMemo(
    () => (data?.failureReasonSummary || []).slice(0, 6),
    [data]
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9fb', py: 4 }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Otamiser Daily Tracking Health
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Last {data?.days || DASHBOARD_DAYS} UTC days by config-day outcome.
              BDC means Booking.com.
            </Typography>
          </Box>
          <Button variant="contained" onClick={fetchDashboard} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <DashboardMetricCard
                  label="Total Configs"
                  value={summary.total.toLocaleString()}
                  helper="Config-day outcomes in range"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DashboardMetricCard
                  label="Succeeded"
                  value={summary.success.toLocaleString()}
                  helper={`${formatPercent(summary.successRate)} success rate`}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DashboardMetricCard
                  label="Failed"
                  value={summary.failed.toLocaleString()}
                  helper="Configs with no same-day success"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DashboardMetricCard
                  label="Last Refreshed"
                  value={
                    data?.generatedAt
                      ? new Date(data.generatedAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'
                  }
                  helper="Generated by Otamiser API"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Success vs Failed Counts
                    </Typography>
                    <Box sx={{ height: 420 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="airbnbSuccess"
                            name="Airbnb Success"
                            stackId="airbnb"
                            fill="#14B8A6"
                          />
                          <Bar
                            dataKey="airbnbFailed"
                            name="Airbnb Failed"
                            stackId="airbnb"
                            fill="#F97316"
                          />
                          <Bar
                            dataKey="bdcSuccess"
                            name="BDC Success"
                            stackId="bdc"
                            fill="#0EA5E9"
                          />
                          <Bar
                            dataKey="bdcFailed"
                            name="BDC Failed"
                            stackId="bdc"
                            fill="#EF4444"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Top Failure Reasons
                    </Typography>
                    {topReasons.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No unresolved failures in this window.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {topReasons.map((reason, idx) => (
                          <Box key={`${reason.date}-${reason.source}-${reason.reason}-${idx}`}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {sourceLabel(reason.source)} · {formatDateLabel(reason.date)}
                              </Typography>
                              <Chip size="small" label={reason.count} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {reason.reason}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ mt: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Failed Configs
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Listing ID</TableCell>
                        <TableCell>Config ID</TableCell>
                        <TableCell>Attempts</TableCell>
                        <TableCell>Latest Failure</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.failures || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No unresolved failures in this window.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (data?.failures || []).map(row => (
                          <TableRow
                            key={`${row.date}-${row.source}-${row.listingID}-${row.rankingConfigID}`}
                          >
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{sourceLabel(row.source)}</TableCell>
                            <TableCell>{row.listingID}</TableCell>
                            <TableCell>{row.rankingConfigID}</TableCell>
                            <TableCell>{row.failedAttempts}</TableCell>
                            <TableCell>{row.latestFailureAt || '-'}</TableCell>
                            <TableCell>{row.reason}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
};

export default OtamiserHealthDashboard;
